/**
 * Share snapshots (plan Task 7.16, §3.7 rule 5): offered exactly where the attachment rule and the gate allow
 * `snapshot-list` — anywhere else the dialog shows that sentence and sends nothing. The list is newest first in local
 * time; Create re-reads it; Open opens the snapshot's files tab; Delete asks through a plain confirm naming the time.
 * Create / Delete follow the gate: Microsoft Entra is attempted (I2) and a 403 shows main's role sentence with its chip.
 */
import { useState } from 'react';
import { Button } from '../../../../../components/ui/button';
import { ReasonButton } from '../../../../../components/ui/ReasonButton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { RandomDotmSpinner } from '../../../../../components/ui/RandomDotmSpinner';
import { useConfirmDialogStore } from '../../../common/components/confirm/confirmDialogStore';
import type { StorageOperationError } from '../../../../../types/azureStorage';
import { formatStorageTimestamp } from '../../utils/storageItemRows';
import type { ShareDialogRequest } from '../fileDialogStore';
import { startShareRequest } from '../shareApi';
import { openShareSnapshotTab, shareActionOutcome, trackShareAction } from '../shareActions';
import { ShareErrorNotice } from './ShareErrorNotice';
import { useShareRefusal } from './shareGate';
import { openSnapshotDeleteConfirm } from './snapshotDeleteConfirm';
import { useShareOperation } from './useShareOperation';
import { useShareRead } from './useShareRead';

type Props = { request: ShareDialogRequest; onClose: () => void };

const newestFirst = (left: string, right: string): number => Date.parse(right) - Date.parse(left) || right.localeCompare(left);
/**
 * The snapshot-delete confirm is the app-level AlertDialog, a sibling of this dialog: a click on it would otherwise
 * count as outside and close this dialog, and its `onDeleted` reload would land on nothing (MetaInfoManager's rule).
 */
const keepOpenForConfirm = (event: { target: EventTarget | null; preventDefault(): void }): void => {
  if ((event.target as HTMLElement | null)?.closest?.('[role="alertdialog"]')) event.preventDefault();
};

export function ShareSnapshotsDialog({ request, onClose }: Props) {
  const { connectionId } = request;
  const share = request.resource?.share ?? '';
  const live = { kind: 'share', share } as const;
  const meta = { source: request.source, protocol: request.protocol ?? 'unknown' };
  const refuse = useShareRefusal(connectionId);
  const refusal = refuse('snapshot-list', { protocol: meta.protocol });
  const createRefusal = refuse('snapshot-create', { protocol: meta.protocol });
  const read = useShareRead({
    connectionId, resource: live, purpose: 'snapshots', enabled: !refusal,
    start: () => startShareRequest('shareSnapshots', { connectionId, resource: live }),
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<StorageOperationError>();
  const { begin, settle, abandon, isActive } = useShareOperation();
  const snapshots = [...(read.data?.snapshots ?? [])].sort((left, right) => newestFirst(left.snapshot, right.snapshot));

  const createSnapshot = async () => {
    if (isActive() || createRefusal) return;
    setCreating(true);
    setError(undefined);
    const handle = startShareRequest('shareSnapshotCreate', { connectionId, resource: live });
    begin(handle, 'snapshot_create', meta);
    const result = await handle.promise;
    if (!settle(handle)) return;
    setCreating(false);
    trackShareAction('snapshot_create', shareActionOutcome(result.ok ? undefined : result.error), meta);
    if (result.ok) read.reload();
    else setError(result.error);
  };
  const openSnapshot = (snapshot: string) => {
    if (openShareSnapshotTab({ connectionId, share, snapshot, ...meta })) onClose();
  };
  const dismiss = () => { abandon(); onClose(); };
  // UI/UX fix round A (U34): while the delete confirm is open this dialog is hidden — it used to show as an empty card
  // peeking above and below the confirm. It stays mounted (the confirm's onDeleted reload lands on it).
  const covered = useConfirmDialogStore((state) => state.isOpen);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) dismiss(); }}>
      <DialogContent className={covered ? 'max-w-lg invisible' : 'max-w-lg'} data-covered={covered || undefined} data-testid="share-snapshots-dialog" onPointerDownOutside={keepOpenForConfirm} onInteractOutside={keepOpenForConfirm}>
        <DialogHeader>
          <DialogTitle>Snapshots of {share}</DialogTitle>
          <DialogDescription>Read-only, point-in-time copies of the whole share. Times are local.</DialogDescription>
        </DialogHeader>
        {refusal ? <p className="text-sm text-muted-foreground" data-testid="share-snapshots-refusal">{refusal}</p> : <>
          {read.isLoading && <p className="text-sm text-muted-foreground" data-testid="share-snapshots-loading">Reading snapshots…</p>}
          {read.error && <ShareErrorNotice error={read.error} testId="share-snapshots-read-error" />}
          {read.data && snapshots.length === 0 && <p className="text-sm text-muted-foreground" data-testid="share-snapshots-empty">This share has no snapshots.</p>}
          {snapshots.length > 0 && (
            <ul className="max-h-72 divide-y divide-border overflow-auto rounded border border-border" data-testid="share-snapshots-list">
              {snapshots.map(({ snapshot }) => {
                const deleteRefusal = refuse('snapshot-delete', { protocol: meta.protocol, snapshot });
                return (
                  <li key={snapshot} className="flex items-center gap-2 px-2 py-1 text-sm" data-testid="share-snapshot-row">
                    <span className="flex-1 truncate" title={snapshot}>{formatStorageTimestamp(snapshot)}</span>
                    <Button type="button" size="xs" variant="outline" onClick={() => openSnapshot(snapshot)} data-testid="share-snapshot-open">Open</Button>
                    <ReasonButton
                      type="button" size="xs" variant="ghost" reason={deleteRefusal} title="Delete this snapshot" data-testid="share-snapshot-delete"
                      onClick={() => openSnapshotDeleteConfirm({ connectionId, share, snapshot, ...meta, onDeleted: read.reload })}
                    >Delete…</ReasonButton>
                  </li>
                );
              })}
            </ul>
          )}
          {error && <ShareErrorNotice error={error} testId="share-snapshots-error" />}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={dismiss} data-testid="share-snapshots-close">Close</Button>
            <ReasonButton type="button" reason={createRefusal} disabled={creating} onClick={() => void createSnapshot()} data-testid="share-snapshots-create">
              {creating && <RandomDotmSpinner size={14} className="mr-1" />}Create snapshot
            </ReasonButton>
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}
